// src/pages/admin/AdminDisputes.js
// LOT 5d : Admin — litiges / réclamations clients (le marchand ne décide jamais).
// Maxula

import React, { useEffect, useState, useCallback } from 'react';
import { FiTool, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { disputeAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toFixed(2);
const STATUS = {
  open: { label: 'Ouverte', cls: 'bg-amber-100 text-amber-800' },
  in_review: { label: 'En cours', cls: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'Résolue', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejetée', cls: 'bg-red-100 text-red-700' }
};
const REASON_LABELS = {
  not_received: 'Non reçu',
  damaged: 'Endommagé',
  not_as_described: 'Non conforme à la description',
  other: 'Autre'
};
const FILTERS = ['open', 'in_review', 'resolved', 'rejected', 'all'];
const FILTER_LABELS = { open: 'Ouvertes', in_review: 'En cours', resolved: 'Résolues', rejected: 'Rejetées', all: 'Toutes' };

const AdminDisputes = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [decide, setDecide] = useState(null); // { d }
  const [status, setStatus] = useState('in_review');
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (st) => {
    try {
      setLoading(true);
      const res = await disputeAPI.adminList(st === 'all' ? {} : { status: st });
      setItems(res.data?.data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(filter); }, [filter, load]);

  const openDecision = (d) => { setDecide({ d }); setStatus(d.status === 'open' ? 'in_review' : d.status); setResolution(d.resolution || ''); };

  const submitDecision = async () => {
    if (!decide) return;
    setBusy(true);
    try {
      const r = await disputeAPI.adminDecide(decide.d.id, { status, resolution });
      toast.success(r?.data?.message || 'Réclamation mise à jour');
      setDecide(null); setResolution('');
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  const openCount = items.filter((d) => d.status === 'open').length;

  return (
    <div className="p-6 pb-24">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Litiges &amp; réclamations</h1>
      <p className="text-gray-500 mb-4">
        Réclamations ouvertes par les clients pendant la fenêtre de traçabilité. Vous seul tranchez ; le vendeur est informé.
        {filter === 'open' && openCount > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">{openCount} ouverte(s)</span>}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${filter === f ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-400">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-400">Aucune réclamation {FILTER_LABELS[filter]?.toLowerCase()}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Commande</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-right px-4 py-3 font-medium">Montant</th>
                <th className="text-left px-4 py-3 font-medium">Motif</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Statut</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 font-mono font-semibold">{d.order?.orderNumber || `#${d.orderId}`}</td>
                  <td className="px-4 py-3">
                    {(d.user?.firstName || '') + ' ' + (d.user?.lastName || '')}
                    <div className="text-xs text-gray-400">{d.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{fmt(d.order?.total)} MAD</td>
                  <td className="px-4 py-3">
                    {REASON_LABELS[d.reason] || d.reason}
                    {d.description && <div className="text-xs text-gray-500 mt-1 max-w-xs whitespace-pre-wrap">{d.description}</div>}
                    {d.resolution && <div className="text-xs text-indigo-600 mt-1 max-w-xs">Résolution : {d.resolution}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{d.createdAt ? new Date(d.createdAt).toLocaleDateString('fr-FR') : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS[d.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS[d.status]?.label || d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDecision(d)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700">
                      <FiTool size={13} /> Traiter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de traitement */}
      {decide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
              <FiAlertCircle className="text-amber-600" /> Traiter la réclamation
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Commande <span className="font-mono font-semibold">{decide.d.order?.orderNumber}</span> — {fmt(decide.d.order?.total)} MAD
              <span className="block mt-1">Motif : {REASON_LABELS[decide.d.reason] || decide.d.reason}</span>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-200">
              <option value="in_review">En cours d'examen</option>
              <option value="resolved">Résolue</option>
              <option value="rejected">Rejetée</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note de résolution (communiquée au client)</label>
            <textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder="Décision et mesures prises..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-primary-200" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDecide(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
              <button type="button" disabled={busy} onClick={submitDecision}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">
                {busy ? 'Traitement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisputes;
